import { useEffect, useRef } from 'react';
import constraintProfilesStore from 'shared/store/constraintProfilesStore';
import constraintsStore from 'shared/store/constraintsStore';
import chatStore from 'shared/store/chatStore';
import { extractImportContext } from 'pages/assistant/components/activity-panel/panels/constraints/lifecycle/importContext';
import { CONSTRAINTS } from 'pages/assistant/components/activity-panel/panels/constraints/constants';
import { createLogger } from 'shared/logger';

const log = createLogger('SessionConstraints');

/**
 * One-shot session-startup loader for the Vincoli panel's catalog + predefiniti.
 *
 * Watches `userLogged`. On the first `false → true` transition per session,
 * fetches the profile catalog and predefiniti for BOTH modes in parallel, then
 * marks the catalog `loaded`. On `true → false`, clears all session-scoped
 * state in both stores so the next login starts fresh.
 *
 * Pre-loading both modes lets the user click "New chat" (which resets to
 * `builder`) or switch threads (which can be either mode) without any
 * additional network round trip — `setMode` swaps the active predefiniti
 * pointer from cache, and `profilesByMode[mode]` is already populated.
 *
 * SSO context: when the JWT carried an imported portfolio (rebalancing login),
 * its `financial_plan` is used as the rebalancing predefiniti plan and its
 * `risk_profile` is overlaid onto the cached rebalancing predefiniti
 * (read-only-from-SSO invariant). Credential logins have no imported portfolio
 * → defaults to plan 6 with no risk-profile overlay.
 */
export const useSessionConstraints = (userLogged: boolean): void => {
  const inFlightRef = useRef(false);

  useEffect(() => {
    const profiles = constraintProfilesStore.getState();

    if (userLogged && !profiles.loaded && !inFlightRef.current) {
      inFlightRef.current = true;
      const run = async () => {
        try {
          // SSO context (only present on JWT-with-portfolio login). We read it
          // BEFORE the awaits to avoid races with thread navigation.
          const pendingMeta = chatStore.getState().pendingInitialMessage?.metadata;
          const imported = pendingMeta?.[CONSTRAINTS.IMPORTED_PORTFOLIO_KEY];
          const { financialPlan, riskProfile } = extractImportContext(imported);
          const rebalancingPlan = financialPlan ?? 6;

          // Bulk-prefetch everything once. Profiles (both modes) + ALL 8
          // plans of predefiniti per mode. Subsequent plan switches and
          // rebalancing-thread imports become pure cache lookups — no
          // further /predefiniti calls for the lifetime of the session.
          await Promise.all([
            constraintProfilesStore.getState().fetchProfiles('builder'),
            constraintProfilesStore.getState().fetchProfiles('rebalancing'),
            constraintsStore.getState().fetchAllPredefinitiForMode('builder'),
            constraintsStore.getState().fetchAllPredefinitiForMode('rebalancing'),
          ]);

          // Set the initial active predefiniti from the cache. Default to
          // builder mode plan 6; the rebalancing path is activated later by
          // `useConstraintsMetadata` once a thread's imported-portfolio
          // metadata arrives (with its plan + SSO risk_profile).
          constraintsStore.getState().setActivePredefiniti('builder', 6);
          // If this login carried an SSO portfolio, also pre-activate the
          // rebalancing predefiniti for the JWT plan + risk profile so a
          // straight-to-thread navigation (no intermediate /assistant stop)
          // already has the right active pointer.
          if (financialPlan != null) {
            constraintsStore
              .getState()
              .setActivePredefiniti('rebalancing', rebalancingPlan, riskProfile);
          }

          constraintProfilesStore.getState().setLoaded(true);
        } catch (error) {
          log.error('load session constraints failed', { error });
        } finally {
          inFlightRef.current = false;
        }
      };
      void run();
    }

    if (!userLogged) {
      constraintProfilesStore.getState().clearSession();
      constraintsStore.getState().clearSession();
    }
  }, [userLogged]);
};
